import React, { useEffect } from 'react';
import { AxiosRequestConfig } from 'axios';
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getPageContent } from 'features/content/reducer';
import { ComponentsMap } from 'common/maps/components.map';
import { selectPage } from 'features/content/selectors';
import { ENV_VAR } from 'app/api';
import { getSearchWithoutLocale } from 'common/helpers/resolvePath';
import { PAGE_TITLE } from 'App';
import { getParamValue } from 'common/helpers/urlParams';
import { selectDataLoading } from 'features/core/selectors';
import ContentLoader from 'components/ContentLoader/ContentLoader';

interface ILocationState {
  noRefresh?: boolean;
  isPostRequest?: boolean;
  body?: any; // Request payload
  config?: AxiosRequestConfig; // Request config
}

const TemplateSelector = () => {
  const location = useLocation();
  const { pathname, search } = location;
  const params: any = useParams();
  const dispatch = useAppDispatch();
  const queryString =
    process.env.NODE_ENV === ENV_VAR.DEVELOPMENT
      ? getSearchWithoutLocale(search)
      : search; // Only for local development purpose
  const data: any = useAppSelector(selectPage(pathname + queryString));
  // Check is the data loader should be displayed
  const isDataLoading: boolean = useAppSelector(selectDataLoading);
  const locationState = location.state as ILocationState;
  const languageFromQuery = getParamValue(search, 'language');
  const language = params?.lang || languageFromQuery;

  useEffect(() => {
    const langPath = language ? `/${language}` : '/';
    let url = pathname + queryString;
    if (locationState && locationState.noRefresh) {
      return;
    }
    if (locationState) {
      const { isPostRequest, body, config } = locationState;
      dispatch(getPageContent(url, langPath, isPostRequest, body, config));
    } else {
      dispatch(getPageContent(url, langPath));
    }
  }, [dispatch, location, params.lang]);

  if (isDataLoading) {
    return <ContentLoader language={language} />
  }
  if (data) {
    let PageTypeNameValue: string;
    // Use PageTypeName property from PageData to display proper template
    if(data?.PageData === null){
      PageTypeNameValue = data?.Model?.VerifyIdentityViewModelWithoutReference?.PageData?.PageTypeName;
    }
    else{
      PageTypeNameValue = data?.PageData?.PageTypeName;
    }
    // Get template from components map
    const PageComponent = ComponentsMap.get(PageTypeNameValue);
    return PageComponent ? (
      <>
        <Helmet>
          <title>{`${PAGE_TITLE} - ${data?.PageData?.PageTitle}`}</title>
          {data?.PageData?.MetaDescription && (
            <meta
              name="description"
              content={data?.PageData?.MetaDescription}
            />
          )}
          {data?.PageData?.MetaAuthor && (
            <meta name="author" content={data?.PageData?.MetaAuthor} />
          )}
        </Helmet>
        <PageComponent {...data} />
      </> ) :
      null
  }
  return <ContentLoader language={language} />;
};

export default TemplateSelector;
